import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MODULES, ROLE_LABELS } from "../lib/modules";
import Masthead from "../components/Masthead";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import OrgDialog from "../components/admin/OrgDialog";
import UserDialog from "../components/admin/UserDialog";
import { Plus, Pencil, Trash2, KeyRound, Copy } from "lucide-react";

const pill = (bg, fg) => ({ background: bg, color: fg, border: `1px solid ${fg}33`, borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700 });

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState(isSuper ? "orgs" : "users");
  const [orgDlg, setOrgDlg] = useState({ open: false, org: null });
  const [userDlg, setUserDlg] = useState({ open: false, user: null });
  const [cred, setCred] = useState(null); // {email, initial_password}

  const load = useCallback(async () => {
    const [o, u] = await Promise.all([
      apiGet("/admin/organizations").catch(() => ({ organizations: [] })),
      apiGet("/admin/users"),
    ]);
    setOrgs(o.organizations || []);
    setUsers(u.users || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const orgName = (id) => orgs.find((o) => o.id === id)?.name || "—";

  const toggleOrgModule = async (org, key) => {
    const has = (org.modules || []).includes(key);
    const modules = has ? org.modules.filter((k) => k !== key) : [...(org.modules || []), key];
    await apiPatch(`/admin/organizations/${org.id}`, { modules });
    load();
  };

  const delOrg = async (org) => {
    if (!window.confirm(`Delete organization "${org.name}"?`)) return;
    try { await apiDelete(`/admin/organizations/${org.id}`); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Could not delete."); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    try { await apiDelete(`/admin/users/${u.id}`); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Could not delete."); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for "${u.email}"? A new temporary password will be generated.`)) return;
    const res = await apiPost(`/admin/users/${u.id}/reset-password`);
    setCred({ email: u.email, initial_password: res.initial_password });
  };

  return (
    <>
      <Masthead
        eyebrow="Administration"
        title="Admin Panel"
        subtitle="Manage organizations, users and module entitlements"
        meta={<span><strong>{orgs.length}</strong> orgs · <strong>{users.length}</strong> users</span>}
      />
      <div className="ciso flex-1" style={{ padding: "22px 26px 48px" }}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="admin-tabs">
            {isSuper && <TabsTrigger value="orgs" data-testid="tab-orgs">Organisations</TabsTrigger>}
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            {isSuper && <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>}
          </TabsList>

          {/* -------- Organisations -------- */}
          {isSuper && (
            <TabsContent value="orgs" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button data-testid="new-org-btn" onClick={() => setOrgDlg({ open: true, org: null })}
                  style={{ background: "var(--cv-crimson)", color: "#fff" }}>
                  <Plus className="w-4 h-4 mr-1" /> New organization
                </Button>
              </div>
              <div className="ciso-panel" style={{ padding: 0, overflow: "hidden" }}>
                <Table data-testid="orgs-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead><TableHead>Slug</TableHead>
                      <TableHead>Status</TableHead><TableHead>Modules</TableHead>
                      <TableHead>Users</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgs.map((o) => (
                      <TableRow key={o.id} data-testid={`org-row-${o.slug}`}>
                        <TableCell className="font-bold">{o.name}</TableCell>
                        <TableCell style={{ color: "var(--cv-dark-grey)" }}>{o.slug}</TableCell>
                        <TableCell>
                          <span style={o.status === "active" ? pill("rgba(62,124,79,.12)", "#2F6B41") : pill("rgba(165,48,15,.10)", "#A5300F")}>{o.status}</span>
                        </TableCell>
                        <TableCell>{(o.modules || []).length} / {MODULES.length}</TableCell>
                        <TableCell>{o.user_count}</TableCell>
                        <TableCell className="text-right">
                          <button className="p-1.5" data-testid={`edit-org-${o.slug}`} onClick={() => setOrgDlg({ open: true, org: o })}><Pencil className="w-4 h-4" /></button>
                          <button className="p-1.5" data-testid={`del-org-${o.slug}`} onClick={() => delOrg(o)} style={{ color: "var(--cv-crimson)" }}><Trash2 className="w-4 h-4" /></button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          )}

          {/* -------- Users -------- */}
          <TabsContent value="users" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button data-testid="new-user-btn" onClick={() => setUserDlg({ open: true, user: null })}
                style={{ background: "var(--cv-crimson)", color: "#fff" }}>
                <Plus className="w-4 h-4 mr-1" /> New user
              </Button>
            </div>
            <div className="ciso-panel" style={{ padding: 0, overflow: "hidden" }}>
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email</TableHead>
                    <TableHead>Role</TableHead><TableHead>Organization</TableHead>
                    <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                      <TableCell className="font-bold">{u.name || "—"}</TableCell>
                      <TableCell style={{ color: "var(--cv-dark-grey)" }}>{u.email}</TableCell>
                      <TableCell><span style={pill("rgba(213,88,22,.12)", "#B4471F")}>{ROLE_LABELS[u.role] || u.role}</span></TableCell>
                      <TableCell>{orgName(u.org_id)}</TableCell>
                      <TableCell>
                        <span style={(u.status || "active") === "active" ? pill("rgba(62,124,79,.12)", "#2F6B41") : pill("rgba(165,48,15,.10)", "#A5300F")}>{u.status || "active"}</span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <button className="p-1.5" title="Edit" data-testid={`edit-user-${u.email}`} onClick={() => setUserDlg({ open: true, user: u })}><Pencil className="w-4 h-4" /></button>
                        <button className="p-1.5" title="Reset password" data-testid={`resetpw-${u.email}`} onClick={() => resetPw(u)}><KeyRound className="w-4 h-4" /></button>
                        <button className="p-1.5" title="Delete" data-testid={`del-user-${u.email}`} onClick={() => delUser(u)} style={{ color: "var(--cv-crimson)" }}><Trash2 className="w-4 h-4" /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* -------- Modules matrix -------- */}
          {isSuper && (
            <TabsContent value="modules" className="mt-4">
              <div className="ciso-panel" style={{ overflowX: "auto" }}>
                <p className="text-[13px] mb-3" style={{ color: "var(--cv-dark-grey)" }}>
                  Toggle which modules each organization can access. Users inherit these unless individually restricted.
                </p>
                <table className="w-full" data-testid="modules-matrix" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th className="text-left text-[12px] small-caps p-2" style={{ position: "sticky", left: 0, background: "#fff" }}>Organization</th>
                      {MODULES.map((m) => (
                        <th key={m.key} className="text-[11px] p-2" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", color: "var(--cv-dark-grey)" }}>{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((o) => (
                      <tr key={o.id} style={{ borderTop: "1px solid var(--cv-line)" }} data-testid={`matrix-row-${o.slug}`}>
                        <td className="p-2 font-bold text-[13px]" style={{ position: "sticky", left: 0, background: "#fff" }}>{o.name}</td>
                        {MODULES.map((m) => (
                          <td key={m.key} className="p-2 text-center">
                            <Switch
                              checked={(o.modules || []).includes(m.key)}
                              onCheckedChange={() => toggleOrgModule(o, m.key)}
                              data-testid={`matrix-${o.slug}-${m.key}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      <OrgDialog open={orgDlg.open} org={orgDlg.org} onOpenChange={(v) => setOrgDlg((s) => ({ ...s, open: v }))} onSaved={load} />
      <UserDialog open={userDlg.open} user={userDlg.user} orgs={orgs} actor={user}
        onOpenChange={(v) => setUserDlg((s) => ({ ...s, open: v }))} onSaved={load} onCreated={(res) => setCred({ email: res.user.email, initial_password: res.initial_password })} />

      {/* Credential reveal */}
      <Dialog open={!!cred} onOpenChange={(v) => { if (!v) setCred(null); }}>
        <DialogContent className="max-w-[440px]" data-testid="cred-dialog">
          <DialogHeader><DialogTitle className="small-caps">Temporary password</DialogTitle></DialogHeader>
          <p className="text-[13px]" style={{ color: "var(--cv-dark-grey)" }}>
            Share this with <strong>{cred?.email}</strong>. It is shown only once — they'll be asked to change it at first login.
          </p>
          <div className="flex items-center gap-2 mt-2 p-3 rounded" style={{ background: "var(--cv-sand)", border: "1px solid var(--cv-line)" }}>
            <code className="flex-1 text-[15px]" data-testid="cred-password">{cred?.initial_password}</code>
            <button onClick={() => navigator.clipboard?.writeText(cred?.initial_password || "")} title="Copy"><Copy className="w-4 h-4" /></button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCred(null)} data-testid="cred-close" style={{ background: "var(--cv-crimson)", color: "#fff" }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
